// SCALA: Ask Shopify Liquid for the CURRENT stock of this exact variant.
// The public /products/handle.js response does not reliably contain inventory_quantity.
// Only called when the customer clicks an individual Add to cart button.
async function scalaBulkGetRemainingInventory(productUrl, variantId, currentCart) {
  const requestUrl = new URL(productUrl, window.location.origin);
  if (requestUrl.origin !== window.location.origin) return null;
  requestUrl.searchParams.set('variant', String(variantId));
  requestUrl.searchParams.set('section_id', 'scala-bulk-inventory');

  try {
    const response = await fetch(requestUrl.toString(), { cache: 'no-store' });
    if (!response.ok) throw new Error(`Stock lookup HTTP ${response.status}`);
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const stock = page.querySelector('[data-scala-bulk-inventory]');
    if (!stock || Number(stock.dataset.variantId) !== Number(variantId)) {
      throw new Error('Stock information is unavailable for the selected variant.');
    }
    if (stock.dataset.inventoryManagement !== 'shopify') return null;

    const total = Number(stock.dataset.inventoryQuantity);
    if (!Number.isSafeInteger(total)) throw new Error('Invalid stock quantity.');

    // Match SCALA's existing product-page policy:
    // finite positive stock is capped even when 'continue' is enabled;
    // only an actual backorder (<= 0 and continue) is unlimited.
    if (stock.dataset.stockState === 'backorder' ||
        (total <= 0 && stock.dataset.inventoryPolicy === 'continue')) {
      return null;
    }
    const inCart = (currentCart?.items || [])
      .filter(item => Number(item.variant_id) === Number(variantId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    return Math.max(0, total - inCart);
  } catch (error) {
    // If inventory can't be retrieved, retain Shopify's standard cart check.
    console.warn('SCALA inventory lookup:', error);
    return null;
  }
}

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.scalaRequestId = 0;
    this.scalaBulkController = new AbortController();
    this.scalaInstanceId = (this.input.id || 'search') + '-' + Math.random().toString(36).slice(2, 8);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }


  getLocaleRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  }


getBulkSearchUrl(searchTerm = '') {
  const url = this.getLocaleRoot() + 'pages/scala-bulk-search';

  if (!searchTerm) return url;

  const params = new URLSearchParams();
  params.set('codes', searchTerm);

  return url + '?' + params.toString();
}


  // Only a list of code-shaped entries activates bulk search in the header.
  // A space in a product name is NOT sufficient. The dedicated bulk page
  // remains available for manual searches, including unusual SKU formats.
  getBulkCodes(searchTerm = this.getQuery()) {
    const entries = String(searchTerm).trim().split(/[,;\s]+/).filter(Boolean);
    // A single numeric model number (for example 10350) should also display
    // its SKU variants in the header. Other single searches stay unchanged.
if (entries.length < 2) return [];

    const codes = [];
    for (const entry of entries) {
      const parts = entry.split('#');
      if (parts.length > 2) return [];
      const code = parts[0];
      if (parts.length === 2 && (!/^(0|[1-9]\d*)$/.test(parts[1]) ||
          !Number.isSafeInteger(Number(parts[1])))) return [];

      // Numeric SKUs / barcodes; SCALA numeric-prefix SKUs; letter-prefix
      // codes with digits. Ordinary words and hyphenated words do not match.
      const isCode = /^\d{4,14}$/.test(code) ||
        /^\d{5,}-[a-z0-9]+(?:[-._/][a-z0-9]+)*$/i.test(code) ||
        /^[a-z]{1,8}\d{3,}(?:[-._][a-z0-9]+)*$/i.test(code);
      if (!isCode) return [];
      codes.push(code);
    }
    return codes;
  }

  isBulkPageQuery(searchTerm, allowPrefix = false) {
    const query = String(searchTerm).toLowerCase().trim()
      .replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');
    const aliases = [
      'bulk', 'bulk search', 'search bulk', 'scala bulk search',
      'bulk searh', 'bulk seach', 'bulk serach', 'bulksearch',
      'bulk add', 'bulk adding', 'bulk order', 'bulk ordering',
      'bulk zoeken', 'bulk toevoegen', 'bulk bestellen',
      'meerdere producten toevoegen', 'bulk suche', 'sammelbestellung',
      'recherche en lot', 'commande groupee', 'busqueda masiva',
      'ricerca multipla', 'ordine multiplo'
    ];
    if (aliases.includes(query)) return true;
    return allowPrefix && query.length >= 4 &&
      aliases.some(alias => alias.startsWith(query));
  }

  cancelSearchRequests() {
    this.scalaRequestId += 1;
    this.abortController.abort();
    this.scalaBulkController.abort();
    this.abortController = new AbortController();
    this.scalaBulkController = new AbortController();
    return this.scalaRequestId;
  }

  disconnectedCallback() {
    this.cancelSearchRequests();
    clearTimeout(this.scalaStatusTimer);
  }

  async getBulkProductData(handle, context) {
    const cache = context?.products;
    if (cache?.has(handle)) return cache.get(handle);
    const pending = (async () => {
      const response = await fetch(
        this.getLocaleRoot() + 'products/' + encodeURIComponent(handle) + '.js',
        { signal: context?.signal }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })();
    if (cache) cache.set(handle, pending);
    try {
      return await pending;
    } catch (error) {
      if (cache?.get(handle) === pending) cache.delete(handle);
      throw error;
    }
  }

  getSkuFamily(searchCode) {
    const code = searchCode.trim();

    if (!code.includes('-')) {
      return null;
    }

    const family = code.split('-')[0].trim();
    return family.length >= 3 ? family : null;
  }

async fetchBulkProducts(codes, options = {}) {
  const context = { products: new Map(), signal: options.signal };
  const results = [];
  const batchSize = 10;
  const totalProducts = codes.length;
  let completedProducts = 0;

  const loading = document.getElementById('scala-bulk-page-loading');


const updateProgress = () => {
  if (!options.header && loading && !loading.hidden) {
    const progress = document.getElementById('scala-bulk-search-progress');

    if (progress) {
      progress.textContent = `${completedProducts} / ${totalProducts}`;
    }
  }
};


  updateProgress();

  for (let index = 0; index < codes.length; index += batchSize) {
    if (context.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const batch = codes.slice(index, index + batchSize);

    const batchResults = await Promise.all(
      batch.map(async (code) => {
        try {
          const params = new URLSearchParams({
            q: code,
            'resources[type]': 'product',
            'resources[limit]': '10'
          });

          const response = await fetch(
            `${this.getLocaleRoot()}search/suggest.json?${params.toString()}`,
            { signal: context.signal }
          );

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const data = await response.json();
          const products = data.resources.results.products || [];

          const exactMatches = await Promise.all(
            products.map((product) =>
              this.findExactVariant(product, code, context)
            )
          );

          let matches = exactMatches.filter(
            (match) => match !== null
          );

          let resultType = matches.length > 0 ? 'exact' : 'not_found';

          // Numeric model numbers (10350, 97390) represent SKU families.
          // Show ALL matching SKU variants returned from these products,
          // not just an exact match, e.g. 10350-RED and 10350-BLACK.
          const isModelNumber = /^\d{4,8}$/.test(String(code).trim());
          if (isModelNumber || (matches.length === 0 && code.includes('-'))) {
            const partialResults = await Promise.all(
              products.map((product) =>
                this.findPartialVariants(product, code, context)
              )
            );
            const familyVariants = partialResults.flat();

            if (isModelNumber) {
              // Preserve any exact result, while de-duplicating variants.
              matches = Array.from(new Map(
                [...familyVariants, ...matches].map(variant =>
                  [String(variant.variantId), variant]
                )
              ).values());
            } else if (matches.length === 0) {
              matches = familyVariants;
            }

            if (familyVariants.length > 0) {
              resultType = 'partial';
            }
          }

          if (matches.length === 0) {
            const family = this.getSkuFamily(code);

            if (family) {
              const familyParams = new URLSearchParams({
                q: family,
                'resources[type]': 'product',
                'resources[limit]': '10'
              });

              const familyResponse = await fetch(
                `${this.getLocaleRoot()}search/suggest.json?${familyParams.toString()}`,
                { signal: context.signal }
              );

              if (!familyResponse.ok) {
                throw new Error(`HTTP ${familyResponse.status}`);
              }

              const familyData = await familyResponse.json();
              const familyProducts = familyData.resources.results.products || [];

              const familyResults = await Promise.all(
                familyProducts.map((product) =>
                  this.findPartialVariants(product, family, context)
                )
              );

              matches = familyResults
                .flat()
                .filter((variant) => variant.available);

              matches = Array.from(
                new Map(
                  matches.map((variant) => [variant.variantId, variant])
                ).values()
              );

              if (matches.length > 0) {
                resultType = 'alternative';
              }
            }
          }

          return {
            code: code,
            products: products,
            matches: matches,
            resultType: resultType
          };
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          console.error('SCALA Search Error:', code, error);

          return {
            code: code,
            products: [],
            error: true
          };
        }
      })
    );

    results.push(...batchResults);
completedProducts += batch.length;
updateProgress();
    if (index + batchSize < codes.length) {
      await new Promise(resolve => setTimeout(resolve, 250));
    }
  }

  const currentCodes = this.getBulkCodes();

    if (
      options.header && !context.signal?.aborted &&
      options.requestId === this.scalaRequestId &&
      currentCodes.length > 0 &&
      currentCodes.join('|') === codes.join('|')
    ) {
      this.renderBulkSearchResults(results);
    }

    return results;
  }

  getScalaMessages() {
    const language = document.documentElement.lang
      .toLowerCase()
      .split('-')[0];

    const translations = {
      en: {
        exact: 'Product found',
        partial: 'Available variants',
        alternative: 'Product not found - available alternatives',
        not_found: 'No products found',
        available: 'Available',
        unavailable: 'Out of stock',
        searchResults: 'Search results'
      },
      nl: {
        exact: 'Product gevonden',
        partial: 'Beschikbare varianten',
        alternative: 'Product niet gevonden - beschikbare alternatieven',
        not_found: 'Geen producten gevonden',
        available: 'Beschikbaar',
        unavailable: 'Uitverkocht',
        searchResults: 'Zoekresultaten'
      },
      de: {
        exact: 'Produkt gefunden',
        partial: 'Verfügbare Varianten',
        alternative: 'Produkt nicht gefunden - verfügbare Alternativen',
        not_found: 'Keine Produkte gefunden',
        available: 'Verfügbar',
        unavailable: 'Ausverkauft',
        searchResults: 'Suchergebnisse'
      },
      fr: {
        exact: 'Produit trouvé',
        partial: 'Variantes disponibles',
        alternative: 'Produit introuvable - alternatives disponibles',
        not_found: 'Aucun produit trouvé',
        available: 'Disponible',
        unavailable: 'Rupture de stock',
        searchResults: 'Résultats de recherche'
      },
      es: {
        exact: 'Producto encontrado',
        partial: 'Variantes disponibles',
        alternative: 'Producto no encontrado - alternativas disponibles',
        not_found: 'No se encontraron productos',
        available: 'Disponible',
        unavailable: 'Agotado',
        searchResults: 'Resultados de búsqueda'
      },
      it: {
        exact: 'Prodotto trovato',
        partial: 'Varianti disponibili',
        alternative: 'Prodotto non trovato - alternative disponibili',
        not_found: 'Nessun prodotto trovato',
        available: 'Disponibile',
        unavailable: 'Esaurito',
        searchResults: 'Risultati di ricerca'
      },
      ru: {
        exact: 'Товар найден',
        partial: 'Доступные варианты',
        alternative: 'Товар не найден - доступные альтернативы',
        not_found: 'Товары не найдены',
        available: 'В наличии',
        unavailable: 'Нет в наличии',
        searchResults: 'Результаты поиска'
      }
    };

    const base = translations[language] || translations.en;
    const stockAdjustedLabels = {
      en: 'Adjusted to available stock',
      nl: 'Aangepast aan beschikbare voorraad',
      de: 'An verfügbaren Bestand angepasst',
      fr: 'Ajusté au stock disponible',
      es: 'Ajustado al stock disponible',
      it: 'Adeguato alla disponibilità',
      ru: 'Скорректировано по наличию'
    };
    const extra = {
      en: ['Pages', 'Add to cart', 'Adding...', 'Added!', 'Try again', 'Quantity', 'Decrease quantity', 'Increase quantity', 'Please enter a valid quantity.', 'Unable to check your shopping cart. Please try again.', 'Your cart supports a maximum of 490 unique products (SKUs/EANs).', 'Unable to add product to cart.'],
      nl: ["Pagina's", 'Toevoegen aan winkelwagen', 'Toevoegen...', 'Toegevoegd!', 'Opnieuw proberen', 'Aantal', 'Aantal verlagen', 'Aantal verhogen', 'Voer een geldig aantal in.', 'Je winkelwagen kan niet worden gecontroleerd. Probeer het opnieuw.', 'Je winkelwagen ondersteunt maximaal 490 unieke producten (SKUs/EANs).', 'Het product kon niet worden toegevoegd.'],
      de: ['Seiten', 'In den Warenkorb legen', 'Wird hinzugef\u00fcgt...', 'Hinzugef\u00fcgt!', 'Erneut versuchen', 'Menge', 'Menge verringern', 'Menge erh\u00f6hen', 'Bitte gib eine g\u00fcltige Menge ein.', 'Dein Warenkorb konnte nicht gepr\u00fcft werden.', 'Dein Warenkorb unterst\u00fctzt maximal 490 unterschiedliche Produkte (SKUs/EANs).', 'Das Produkt konnte nicht hinzugef\u00fcgt werden.'],
      fr: ['Pages', 'Ajouter au panier', 'Ajout en cours...', 'Ajout\u00e9 !', 'R\u00e9essayer', 'Quantit\u00e9', 'Diminuer la quantit\u00e9', 'Augmenter la quantit\u00e9', 'Veuillez saisir une quantit\u00e9 valide.', 'Impossible de v\u00e9rifier votre panier.', 'Votre panier peut contenir au maximum 490 produits diff\u00e9rents (SKU/EAN).', "Impossible d'ajouter le produit au panier."],
      es: ['P\u00e1ginas', 'A\u00f1adir al carrito', 'A\u00f1adiendo...', '\u00a1A\u00f1adido!', 'Intentar de nuevo', 'Cantidad', 'Reducir cantidad', 'Aumentar cantidad', 'Introduce una cantidad v\u00e1lida.', 'No se ha podido comprobar tu carrito.', 'Tu carrito admite un m\u00e1ximo de 490 productos \u00fanicos (SKU/EAN).', 'No se ha podido a\u00f1adir el producto.'],
      it: ['Pagine', 'Aggiungi al carrello', 'Aggiunta in corso...', 'Aggiunto!', 'Riprova', 'Quantit\u00e0', 'Diminuisci quantit\u00e0', 'Aumenta quantit\u00e0', 'Inserisci una quantit\u00e0 valida.', 'Impossibile controllare il carrello.', 'Il carrello pu\u00f2 contenere al massimo 490 prodotti diversi (SKU/EAN).', 'Impossibile aggiungere il prodotto.'],
      ru: ['\u0421\u0442\u0440\u0430\u043d\u0438\u0446\u044b', '\u0414\u043e\u0431\u0430\u0432\u0438\u0442\u044c \u0432 \u043a\u043e\u0440\u0437\u0438\u043d\u0443', '\u0414\u043e\u0431\u0430\u0432\u043b\u0435\u043d\u0438\u0435...', '\u0414\u043e\u0431\u0430\u0432\u043b\u0435\u043d\u043e!', '\u041f\u043e\u0432\u0442\u043e\u0440\u0438\u0442\u044c', '\u041a\u043e\u043b\u0438\u0447\u0435\u0441\u0442\u0432\u043e', '\u0423\u043c\u0435\u043d\u044c\u0448\u0438\u0442\u044c \u043a\u043e\u043b\u0438\u0447\u0435\u0441\u0442\u0432\u043e', '\u0423\u0432\u0435\u043b\u0438\u0447\u0438\u0442\u044c \u043a\u043e\u043b\u0438\u0447\u0435\u0441\u0442\u0432\u043e', '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0434\u043e\u043f\u0443\u0441\u0442\u0438\u043c\u043e\u0435 \u043a\u043e\u043b\u0438\u0447\u0435\u0441\u0442\u0432\u043e.', '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u043f\u0440\u043e\u0432\u0435\u0440\u0438\u0442\u044c \u043a\u043e\u0440\u0437\u0438\u043d\u0443.', '\u0412 \u043a\u043e\u0440\u0437\u0438\u043d\u0435 \u043c\u043e\u0436\u0435\u0442 \u0431\u044b\u0442\u044c \u043d\u0435 \u0431\u043e\u043b\u0435\u0435 490 \u0440\u0430\u0437\u043b\u0438\u0447\u043d\u044b\u0445 \u0442\u043e\u0432\u0430\u0440\u043e\u0432 (SKU/EAN).', '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u0434\u043e\u0431\u0430\u0432\u0438\u0442\u044c \u0442\u043e\u0432\u0430\u0440.']
    };
    const keys = ['pages', 'addToCart', 'adding', 'added', 'tryAgain', 'quantity', 'decrease', 'increase', 'invalidQuantity', 'cartCheckError', 'cartLimit', 'addError'];
    const values = extra[language] || extra.en;
    return Object.assign({}, base, Object.fromEntries(keys.map((key, index) => [key, values[index]])), {
      stockAdjusted: stockAdjustedLabels[language] || stockAdjustedLabels.en
    });
  }

  renderBulkSearchResults(results) {
    if (!this.predictiveSearchResults) return;
    this.dataset.scalaSearchMode = 'bulk';
    this.predictiveSearchResults.replaceChildren();
    const container = document.createElement('div');
    container.id = 'scala-bulk-search-results';
    container.className = 'scala-bulk-search-results';
    this.predictiveSearchResults.appendChild(container);

    const heading = document.createElement('h3');
    heading.textContent = 'SCALA Bulk Search';
    container.appendChild(heading);

    const messages = this.getScalaMessages();

    results.forEach((result) => {
      const group = document.createElement('div');
      group.className = 'scala-bulk-group';

      const title = document.createElement('h4');
      title.textContent = result.code;
      group.appendChild(title);

      const status = document.createElement('p');
      status.textContent =
        messages[result.resultType] || messages.searchResults;
      group.appendChild(status);

      (result.matches || []).forEach((product) => {
        const link = document.createElement('a');
        link.href = product.url;
        link.className = 'scala-bulk-product';

        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = product.productTitle;
          image.width = 60;
          image.height = 60;
          image.style.objectFit = 'contain';
          link.appendChild(image);
        }

        const details = document.createElement('div');

        const name = document.createElement('strong');
        name.textContent = product.productTitle;
        details.appendChild(name);

        const variant = document.createElement('p');
        variant.textContent =
          `${product.variantTitle} | SKU: ${product.sku}`;
        details.appendChild(variant);

        // Hide retailer net prices from unauthorised visitors, like the main UK product page.
        if (window.SCALA_BULK_CAN_ORDER === true) {
          const price = document.createElement('p');
          const currency = window.Shopify?.currency?.active || 'GBP';
          try {
            price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
              style: 'currency', currency
            }).format(Number(product.price));
          } catch (_) {
            price.textContent = '£' + Number(product.price).toFixed(2);
          }
          details.appendChild(price);
        }

        const availability = document.createElement('p');
        availability.textContent = product.available
          ? messages.available
          : messages.unavailable;

        details.appendChild(availability);
        link.appendChild(details);
        group.appendChild(link);

        // Quantity controls
        const controls = document.createElement('div');
        controls.className = 'scala-bulk-order-controls';

        const wrapper = document.createElement('div');
        wrapper.className = 'scala-bulk-quantity-wrapper';

        const minus = document.createElement('button');
        minus.type = 'button';
        minus.className = 'scala-bulk-qty-minus';
        minus.textContent = '−';
        minus.setAttribute('aria-label', messages.decrease);

        const quantity = document.createElement('input');
        quantity.type = 'number';
        quantity.min = '0';
        quantity.step = '1';
        quantity.value = '0';
        quantity.className = 'scala-bulk-quantity';
        quantity.setAttribute('aria-label', messages.quantity);

        const plus = document.createElement('button');
        plus.type = 'button';
        plus.className = 'scala-bulk-qty-plus';
        plus.textContent = '+';
        plus.setAttribute('aria-label', messages.increase);

        minus.addEventListener('click', () => {
          quantity.value = String(
            Math.max(0, Number(quantity.value || 0) - 1)
          );
        });

        plus.addEventListener('click', () => {
          const next = Math.max(0, Number(quantity.value || 0) + 1);
          const cap = quantity.max !== '' ? Number(quantity.max) : null;
          quantity.value = String(cap !== null ? Math.min(next, cap) : next);
        });

        // When a quantity is typed, limit it to Shopify's current
        // sellable inventory as soon as the field is confirmed/left.
        // Also check again on Add to cart because stock can change.
        let latestStockCheck = 0;
        async function clampTypedQuantityToStock() {
          const entered = Number(quantity.value);
          if (!Number.isSafeInteger(entered) || entered < 1 || !product.available) return;
          const checkId = ++latestStockCheck;
          const originalValue = quantity.value;
          try {
            const root = window.Shopify?.routes?.root || '/';
            const cartResponse = await fetch(root + 'cart.js', { cache: 'no-store' });
            if (!cartResponse.ok) return;
            const cart = await cartResponse.json();
            const remaining = await scalaBulkGetRemainingInventory(
              product.url, product.variantId, cart
            );
            if (checkId !== latestStockCheck) return;
            // Clear an old cap when an item switches to unlimited backorder.
            if (remaining === null) {
              quantity.removeAttribute('max');
              return;
            }
            quantity.max = String(remaining);
            if (quantity.value !== originalValue) return;
            if (entered > remaining) {
              quantity.value = String(remaining);
              stockNotice.textContent = `${messages.stockAdjusted}: ${entered} → ${remaining}`;
              stockNotice.hidden = false;
            }
          } catch (error) {
            console.warn('SCALA stock check:', error);
          }
        }
        quantity.addEventListener('change', () => { void clampTypedQuantityToStock(); });

        wrapper.append(minus, quantity, plus);
        controls.appendChild(wrapper);

        // Add to cart
        const addButton = document.createElement('button');
        addButton.type = 'button';
        addButton.className = 'scala-bulk-add-button';
        addButton.textContent = messages.addToCart;

        const stockNotice = document.createElement('p');
        stockNotice.setAttribute('role', 'status');
        stockNotice.style.cssText = 'margin:6px 0 8px;font-size:12px;line-height:1.4;color:#555;';
        stockNotice.hidden = true;
        if (!product.available || !product.variantId) {
          quantity.disabled = true;
          minus.disabled = true;
          plus.disabled = true;
          addButton.disabled = true;
        }

        addButton.addEventListener('click', async (event) => {
          event.preventDefault();
          // Keep the focused button in the menu while adding. A native
          // disabled button loses focus and can close predictive search.
          if (addButton.getAttribute('aria-busy') === 'true') return;

          const amount = Number(quantity.value);

          if (!Number.isSafeInteger(amount) || amount < 1) {
            alert(messages.invalidQuantity);
            return;
          }

          addButton.setAttribute('aria-busy', 'true');
          addButton.setAttribute('aria-disabled', 'true');
          // Safari/touch may not focus a clicked button automatically.
          // Focus stays inside the menu without moving its scroll position.
          addButton.focus({ preventScroll: true });
          addButton.textContent = messages.adding;

          try {
            const root = this.getLocaleRoot();

            // Check the existing shopping cart.
            const cartResponse = await fetch(root + 'cart.js');

            if (!cartResponse.ok) {
              throw new Error(messages.cartCheckError);
            }

            const cart = await cartResponse.json();
            const beforeQuantity = (cart.items || [])
              .filter(item => Number(item.variant_id) === Number(product.variantId))
              .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

            // Keep the existing 490-order-line safety margin.
            const exists = cart.items.some(
              item => Number(item.variant_id) ===
                Number(product.variantId)
            );

            if (!exists && new Set(cart.items.map(item => String(item.variant_id))).size >= 490) {
              alert(messages.cartLimit);
              addButton.textContent = messages.addToCart;
              return;
            }

            let addAmount = amount;
            stockNotice.textContent = '';
            stockNotice.hidden = true;
            const remaining = await scalaBulkGetRemainingInventory(
              product.url, product.variantId, cart
            );
            if (remaining === null) quantity.removeAttribute('max');
            else quantity.max = String(remaining);
            if (remaining !== null && addAmount > remaining) {
              addAmount = remaining;
              quantity.value = String(addAmount);
              stockNotice.textContent = `${messages.stockAdjusted}: ${amount} → ${addAmount}`;
              stockNotice.hidden = false;
            }
            if (addAmount === 0) {
              if (!stockNotice.textContent) stockNotice.textContent = `${messages.stockAdjusted}: 0`;
              stockNotice.hidden = false;
              addButton.textContent = messages.addToCart;
              return;
            }

            const response = await fetch(root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: product.variantId,
                  quantity: addAmount
                }]
              })
            });

            if (!response.ok) {
              const error = await response.json().catch(() => ({}));
              // Verify if Shopify partially added stock before returning 422.
              const checkResponse = await fetch(root + 'cart.js', { cache: 'no-store' });
              const checkedCart = checkResponse.ok ? await checkResponse.json() : null;
              const afterQuantity = (checkedCart?.items || [])
                .filter(item => Number(item.variant_id) === Number(product.variantId))
                .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
              const actuallyAdded = Math.max(0, afterQuantity - beforeQuantity);
              if (actuallyAdded === 0) {
                throw new Error(error.description || messages.addError);
              }
              if (actuallyAdded < addAmount) {
                stockNotice.textContent = `${messages.stockAdjusted}: ${amount} → ${actuallyAdded}`;
                stockNotice.hidden = false;
              }
              quantity.value = String(actuallyAdded);
            }

            addButton.textContent = messages.added;

            // Refresh the original SCALA header cart icon.
            try {
              const headerSection = document.querySelector(
                '.shopify-section.section-header'
              );

              if (!headerSection) {
                throw new Error('SCALA header section not found.');
              }

              const headerSectionId = headerSection.id.replace(
                'shopify-section-',
                ''
              );

              const headerResponse = await fetch(
                root + '?section_id=' + encodeURIComponent(headerSectionId),
                { cache: 'no-store' }
              );

              if (!headerResponse.ok) {
                throw new Error('Unable to refresh SCALA header.');
              }

              const headerHTML = await headerResponse.text();
              const parsed = new DOMParser().parseFromString(
                headerHTML,
                'text/html'
              );

              const newCartIcon = parsed.querySelector('#cart-icon-bubble');
              const currentCartIcon = document.querySelector('#cart-icon-bubble');

              if (!newCartIcon || !currentCartIcon) {
                throw new Error('SCALA cart icon not found.');
              }

              currentCartIcon.innerHTML = newCartIcon.innerHTML;
            } catch (refreshError) {
              console.warn('SCALA cart icon refresh:', refreshError);
            }

            // Refresh the cart page after adding a product.
            if (window.location.pathname.replace(/\/$/, '') === this.getLocaleRoot() + 'cart') {
              const cartItems = document.querySelector('cart-items');

              if (cartItems && typeof cartItems.onCartUpdate === 'function') {
                // cart.js also handles the empty-to-filled transition.
                // Do not reload the page: retain the search results,
                // quantities and the customer's position in the menu.
                try {
                  await cartItems.onCartUpdate();
                } catch (refreshError) {
                  console.warn('SCALA cart view refresh:', refreshError);
                }
              } else {
                console.warn('SCALA: cart view refresh is unavailable; the product was added. Refresh the cart page to update its contents.');
              }
            }

          } catch (error) {
            console.error('SCALA Add to Cart:', error);
            stockNotice.textContent = error.message || messages.addError;
            stockNotice.hidden = false;
            addButton.textContent = messages.tryAgain;
          } finally {
            addButton.removeAttribute('aria-busy');
            addButton.removeAttribute('aria-disabled');
            addButton.disabled = !product.available || !product.variantId;
          }
        });

        controls.appendChild(addButton);
        group.appendChild(controls);
        group.appendChild(stockNotice);
      });

      container.appendChild(group);
    });

    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    this.setLiveRegionText(messages.searchResults);
    this.open();
  }

  async findExactVariant(product, searchCode, context) {
    try {
      const productData = await this.getBulkProductData(product.handle, context);
      const normalizedCode = searchCode.trim().toLowerCase();

      const variant = productData.variants.find((variant) => {
        const sku = String(variant.sku || '').trim().toLowerCase();
        const barcode = String(variant.barcode || '').trim().toLowerCase();

        return sku === normalizedCode || barcode === normalizedCode;
      });

      if (!variant) {
        return null;
      }

      return {
        productId: productData.id,
        productTitle: productData.title,
        variantId: variant.id,
        variantTitle: variant.title,
        sku: variant.sku,
        barcode: variant.barcode,
        price: variant.price / 100,
        image: variant.featured_image?.src || productData.featured_image,
url: `${this.getLocaleRoot()}products/${productData.handle}?variant=${variant.id}`,
available: variant.available,
inventoryQuantity: variant.inventory_quantity,
inventoryManagement: variant.inventory_management,
inventoryPolicy: variant.inventory_policy
      };

    } catch (error) {
      if (error.name === 'AbortError') throw error;
      console.error('SCALA Exact Match Error:', error);
      return null;
    }
  }

  async findPartialVariants(product, searchCode, context) {
    try {
      const productData = await this.getBulkProductData(product.handle, context);
      const normalizedCode = searchCode.trim().toLowerCase();

      const variants = productData.variants.filter((variant) => {
        const sku = String(variant.sku || '').trim().toLowerCase();

        return sku.startsWith(normalizedCode + '-');
      });

      return variants.map((variant) => ({
        productId: productData.id,
        productTitle: productData.title,
        variantId: variant.id,
        variantTitle: variant.title,
        sku: variant.sku,
        barcode: variant.barcode,
        price: variant.price / 100,
        image: variant.featured_image?.src || productData.featured_image,
        url: `${this.getLocaleRoot()}products/${productData.handle}?variant=${variant.id}`,
available: variant.available,
inventoryQuantity: variant.inventory_quantity,
inventoryManagement: variant.inventory_management,
inventoryPolicy: variant.inventory_policy
      }));

    } catch (error) {
      if (error.name === 'AbortError') throw error;
      console.error('SCALA Partial Match Error:', error);
      return [];
    }
  }


  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    const requestId = this.cancelSearchRequests();
    this.searchTerm = newSearchTerm;
    this.close(false);
    this.removeAttribute('results');
    this.predictiveSearchResults?.replaceChildren();
    if (!newSearchTerm) return;

    const bulkCodes = this.getBulkCodes(newSearchTerm);
    this.dataset.scalaSearchMode = bulkCodes.length > 0 ? 'bulk' : 'standard';
    if (bulkCodes.length > 0) {
      this.setLiveRegionLoadingState();
      this.fetchBulkProducts(bulkCodes, {
        header: true, requestId, signal: this.scalaBulkController.signal
      }).catch(error => {
        if (error.name === 'AbortError' || requestId !== this.scalaRequestId) return;
        console.error('SCALA header bulk search:', error);
        this.close(false);
      });
      return;
    }
    this.getSearchResults(newSearchTerm, requestId);
  }

  onFormSubmit(event) {
    const searchTerm = this.getQuery();
    if (!searchTerm || (this.searchTerm === searchTerm && this.querySelector('[aria-selected="true"] a'))) {
      event.preventDefault();
      return;
    }
    if (this.isBulkPageQuery(searchTerm)) {
      event.preventDefault();
      window.location.href = this.getBulkSearchUrl();
      return;
    }
if (this.getBulkCodes(searchTerm).length > 0 &&
    !/^\d{4,8}$/.test(searchTerm)) {
      event.preventDefault();
      window.location.href = this.getBulkSearchUrl(searchTerm);
    }
    // Otherwise the existing Shopify search form submits the complete phrase.
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.cancelSearchRequests();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.onChange();
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.target.closest('.scala-bulk-order-controls')) return;
    if (!this.getQuery().length) this.close(true);

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        if (this.searchTerm === this.getQuery()) this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.target.closest('.scala-bulk-order-controls')) return;
    // Prevent cursor movement with arrow keys.
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }


  updateSearchForTerm(previousTerm, newTerm) {
    const element = this.querySelector('[data-predictive-search-search-for-text]');
    const currentText = element?.innerText;
    if (!previousTerm || !currentText) return;
    if (currentText.split(previousTerm).length > 2) return;
    element.innerText = currentText.replace(previousTerm, () => newTerm);
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;
    const options = Array.from(this.querySelectorAll('[role="option"]'))
      .filter(element => element.offsetParent !== null && !element.closest('[hidden]'));
    if (!options.length) return;
    const selected = this.querySelector('[aria-selected="true"]');
    const currentIndex = options.indexOf(selected);
    if (direction === 'up' && currentIndex < 0) return;
    const index = currentIndex < 0 ? 0 :
      (currentIndex + (direction === 'up' ? -1 : 1) + options.length) % options.length;
    const active = options[index];
    if (!active || active === selected) return;
    if (selected) selected.setAttribute('aria-selected', 'false');
    active.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', active.id);
    if (this.statusElement) this.statusElement.textContent = '';
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }


  getSearchResults(searchTerm, requestId = this.scalaRequestId) {
    const queryKey = this.getLocaleRoot() + '|' + searchTerm.toLowerCase();
    const isCurrent = () => requestId === this.scalaRequestId &&
      this.getQuery() === searchTerm && this.getBulkCodes(searchTerm).length === 0;
    this.setLiveRegionLoadingState();

    if (Object.prototype.hasOwnProperty.call(this.cachedResults, queryKey)) {
      if (isCurrent()) this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }
    fetch(
      this.getLocaleRoot() + 'search/suggest?q=' + encodeURIComponent(searchTerm) +
        '&section_id=predictive-search',
      { signal: this.abortController.signal }
    ).then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    }).then(text => {
      if (!isCurrent()) return;
      const section = new DOMParser().parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Predictive search section not found.');
      const markup = section.innerHTML;
      this.allPredictiveSearchInstances.forEach(instance => {
        instance.cachedResults[queryKey] = markup;
      });
      this.renderSearchResults(markup, searchTerm);
    }).catch(error => {
      if (error.name === 'AbortError' || error.code === 20 || !isCurrent()) return;
      console.error('SCALA predictive search:', error);
      if (this.isBulkPageQuery(searchTerm, true)) {
        // The tool shortcut also works if Shopify search is temporarily unavailable.
        this.renderSearchResults('<div id="predictive-search-results" role="listbox"></div>', searchTerm);
      } else {
        this.close(false);
      }
    });
  }

  addBulkPageSuggestion(searchTerm) {
    if (!this.isBulkPageQuery(searchTerm, true)) return;
    const targetUrl = this.getBulkSearchUrl();
    const makeItem = suffix => {
      const item = document.createElement('li');
      item.id = 'scala-bulk-page-option-' + this.scalaInstanceId + '-' + suffix;
      item.className = 'predictive-search__list-item';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      const link = document.createElement('a');
      link.href = targetUrl;
      link.className = 'predictive-search__item link link--text';
      link.tabIndex = -1;
      const title = document.createElement('span');
      title.className = 'predictive-search__item-heading';
      title.textContent = 'SCALA Bulk Search';
      link.appendChild(title);
      item.appendChild(link);
      return item;
    };
    const pageLists = this.predictiveSearchResults.querySelectorAll(
      '#predictive-search-results-pages-list-desktop, #predictive-search-results-pages-list-mobile'
    );
    if (pageLists.length) {
      pageLists.forEach((list, index) => {
        if (!Array.from(list.querySelectorAll('a')).some(link => link.getAttribute('href') === targetUrl)) {
          list.prepend(makeItem(String(index)));
        }
      });
      return;
    }

    const listbox = this.predictiveSearchResults.querySelector('#predictive-search-results') ||
      this.predictiveSearchResults;
    const section = document.createElement('div');
    section.className = 'scala-bulk-page-suggestion';
    const heading = document.createElement('h2');
    heading.id = 'scala-bulk-pages-' + this.scalaInstanceId;
    heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
    heading.textContent = this.getScalaMessages().pages;
    const list = document.createElement('ul');
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-labelledby', heading.id);
    list.appendChild(makeItem('shortcut'));
    section.append(heading, list);
    listbox.prepend(section);
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status');

    this.loadingText =
      this.loadingText ||
      this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }


  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;
    clearTimeout(this.scalaStatusTimer);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    this.scalaStatusTimer = setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm = this.getQuery()) {
    if (!this.predictiveSearchResults) return;
    this.dataset.scalaSearchMode = 'standard';
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.addBulkPageSuggestion(searchTerm);
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(this.isBulkPageQuery(this.getQuery(), true)
      ? 'SCALA Bulk Search' : count?.textContent || '');
  }

  getResultsMaxHeight() {
    const top = this.input.getBoundingClientRect().bottom;
    this.resultsMaxHeight = Math.max(100, window.innerHeight - top - 16);
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) return;
    // Do not reopen a dropdown after the visitor has moved to another control.
    if (!this.contains(document.activeElement)) return;
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute('aria-selected', false);
    }

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

window.SCALA_BulkSearch = {
  search: async (codes) => {
    const searchElement = document.querySelector('predictive-search');

    if (!searchElement) {
      throw new Error('Predictive search element not found');
    }

    return await searchElement.fetchBulkProducts(codes);
  }
};